/**
 * useSmoothUpdateState — hook que devuelve un "display" estable para
 * renderizar el progreso de un update.
 *
 * Sobre el flicker:
 *   - El agente reporta `retrying` con `percent: 0` entre intentos. La UI
 *     anterior reseteaba la barra a 0 y cambiaba el `variant` del
 *     `LinearProgress` de determinate a indeterminate, causando un salto
 *     visible. Acá mantenemos un `percent` monotónico (nunca baja) salvo
 *     que cambie `startedAt` (nuevo flujo).
 *   - El `setInterval(1s)` refresca `now` para que `computeUpdateDisplay`
 *     pueda detectar "stalled" sin que llegue un nuevo mensaje WS.
 *
 * Implementación: usamos el patrón "Adjusting state during rendering"
 * (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
 * que es la forma recomendada de derivar estado de props en React 19. La
 * alternativa con `useRef` + lectura durante render dispara el lint
 * `react-hooks/refs` y no es segura en concurrent rendering.
 */

import { useEffect, useState } from 'react';
import type { Agent, AgentUpdateState } from '@/types/agent';
import {
  computeUpdateDisplay,
  type SmoothUpdateStateOptions,
  type UpdateDisplay,
} from './updateDisplay';

export interface UseSmoothUpdateStateParams {
  state: AgentUpdateState | null;
  agent: Pick<Agent, 'agentId' | 'connected' | 'version'> | null;
  /** Versión "latest" del manifiesto. Ayuda a detectar complete perdido. */
  latestVersion?: string;
  options?: SmoothUpdateStateOptions;
}

interface MonotonicTracker {
  percent: number;
  startedAt: string | null;
}

export function useSmoothUpdateState(params: UseSmoothUpdateStateParams): UpdateDisplay | null {
  const { state, agent, latestVersion, options } = params;

  const [monotonic, setMonotonic] = useState<MonotonicTracker>({
    percent: 0,
    startedAt: null,
  });

  // "Adjusting state during rendering" — patrón recomendado en React 19.
  if (state === null && monotonic.startedAt !== null) {
    setMonotonic({ percent: 0, startedAt: null });
  } else if (state !== null && monotonic.startedAt !== state.startedAt) {
    setMonotonic({ percent: state.percent, startedAt: state.startedAt });
  } else if (state !== null && state.percent > monotonic.percent) {
    setMonotonic({ percent: state.percent, startedAt: state.startedAt });
  }

  // Tick 1s mientras haya un update en curso — para refrescar `now` que
  // dispara la detección de "stalled" / "waiting-restart".
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!state) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [state]);

  if (!state) return null;

  const display = computeUpdateDisplay({
    state,
    agent,
    latestVersion,
    now,
    options,
  });

  if (!display) return null;

  // Si tenemos un valor previo monotónico (porque el agente acaba de reportar
  // `retrying, percent: 0` después de haber avanzado), lo usamos en vez del
  // nuevo, y forzamos `determinate: true` para que la barra no salte a
  // indeterminate y haga flicker visible.
  if (display.percent < monotonic.percent) {
    return {
      ...display,
      percent: monotonic.percent,
      determinate: true,
    };
  }
  return display;
}
